/**
 * GLSL shaders implementing chroma key (green screen removal) using WebGL2.
 *
 * Pipeline:
 *   Vertex shader   → defines a fullscreen triangle covering the viewport
 *   Fragment shader → for each fragment, compares the pixel color against the
 *                     key color in YCbCr space and produces transparency
 *                     proportional to the chroma distance.
 *
 * This shader implements a simple GPU chroma key optimized
 * for real-time video processing.
 *
 * For each fragment (potential pixel on screen):
 *
 * 1. Reads the color of the video frame from the texture (u_texture)
 *
 * 2. Converts RGB → YCbCr using the BT.601 matrix
 *    - Y  = luminance (brightness)
 *    - Cb = blue-difference chrominance
 *    - Cr = red-difference chrominance
 *
 *    For chroma key we ignore luminance (Y) and compare only
 *    in the (Cb, Cr) plane. This makes background detection more robust
 *    against brightness changes, shadows, or video compression.
 *
 * 3. Computes the distance in the CbCr plane between the pixel and the key color
 *
 * 4. Uses smoothstep to create a soft alpha:
 *    - distance < similarity → transparent (alpha = 0)
 *    - distance > similarity + smoothness → opaque (alpha = 1)
 *    - between them → smooth transition at the mask edges
 *
 * 5. Applies "spill suppression": if the pixel has excess green
 *    compared to red and blue, it reduces that green component
 *    to remove the green tint reflected on the subject.
 *
 * Uniform parameters (controlled from the UI):
 *
 *   u_keyColor   → color to remove, in normalized RGB [0,1]
 *   u_similarity → minimum threshold to consider a color background
 *   u_smoothness → width of the edge transition
 *   u_spill      → spill correction intensity
 */

export const FRAGMENT_SHADER = /* glsl */ `#version 300 es
precision mediump float;

in vec2 v_texCoord;           // interpolated UV coordinate
out vec4 fragColor;           // final fragment color (RGBA)

uniform sampler2D u_texture;  // current video frame
uniform vec3 u_keyColor;      // key color in normalized RGB
uniform float u_similarity;   // matching threshold (lower = more aggressive)
uniform float u_smoothness;   // transition width (higher = softer edges)
uniform float u_spill;        // spill correction intensity

// RGB → YCbCr conversion matrix (BT.601 standard, used by H.264).
// Each row produces one component: Y, Cb, Cr.
const mat3 RGB_TO_YCBCR = mat3(
   0.299,    0.587,    0.114,
  -0.168736,-0.331264, 0.5,
   0.5,     -0.418688,-0.081312
);

void main() {

  // 1. Read the video frame color
  vec4 texColor = texture(u_texture, v_texCoord);

  // 2. Convert the pixel and the key color to YCbCr
  vec3 texYCbCr = RGB_TO_YCBCR * texColor.rgb;
  vec3 keyYCbCr = RGB_TO_YCBCR * u_keyColor;

  // 3. Chromatic distance in the CbCr plane (ignore luminance)
  float dist = distance(texYCbCr.yz, keyYCbCr.yz);

  // 4. Generate the alpha mask with a smooth transition
  // alpha represents how opaque the pixel is (0 = transparent, 1 = opaque).
  float alpha = smoothstep(u_similarity, u_similarity + u_smoothness, dist);

  // 5. Spill suppression
  // Green excess is how much G exceeds the largest component between R and B
  float spillAmount = texColor.g - max(texColor.r, texColor.b);

  // We only care about positive excess
  spillAmount = max(spillAmount, 0.0);

  vec3 correctedColor = texColor.rgb;

  // Reduce green
  correctedColor.g -= spillAmount * u_spill;

  // Redistribute a bit to R and B to preserve perceived brightness
  correctedColor.r += spillAmount * u_spill * 0.5;
  correctedColor.b += spillAmount * u_spill * 0.5;

  // Final clamp to keep values in the [0,1] range
  correctedColor = clamp(correctedColor, 0.0, 1.0);

  /**
   * Premultiplied alpha to avoid halos during blending.
   * A halo is an unwanted colored edge around the subject after chroma key.
   * It occurs because semi-transparent edge pixels still contain color
   * from the original background (for example green).
   * Multiplying RGB by alpha attenuates that residual color before
   * the GPU blends it with the background.
   */
  fragColor = vec4(correctedColor * alpha, alpha);
}
`;

/**
 * Parameters controlling the chroma key fragment shader.
 * These values map directly to the shader uniforms:
 *
 *   keyColor   → u_keyColor
 *   similarity → u_similarity
 *   smoothness → u_smoothness
 *   spill      → u_spill
 */
export type ChromaKeyParams = {
  /** Key color in normalized RGB (0–1). Example: [0, 1, 0] for pure green */
  keyColor: [number, number, number];

  /** Distance threshold in CbCr space to classify a pixel as background */
  similarity: number;

  /** Width of the soft transition between background and foreground */
  smoothness: number;

  /** Spill suppression strength (reduces reflected green on the subject) */
  spill: number;
};

/** Reasonable defaults for a typical green screen (#00ff00) */
export const DEFAULT_CHROMA_KEY_PARAMS: ChromaKeyParams = {
  keyColor: [0, 1, 0], // pure green in normalized RGB
  similarity: 0.15,
  smoothness: 0.1,
  spill: 0.3
};
